// B-139 — Reportes' "Equipo" tab: how Brinks' own team is doing on a client. Not what the client is
// shown (that is the "Cliente" tab, with its committee and its presentation): the work that is
// waiting, who did what and when, how long an alert waits to be attended, the alerts coming in and
// going out, and how each day went through. Drawn in the Cliente tab's look — headline figures with
// their trend, titled cards — with its own period (7 / 30 / 90 días, a month, a range of 92 días at
// most) and a filter by person that narrows every figure a person makes.
//
// One `getCierreTeamActivity` per period; the queue is always today's. The backend asks
// `cierres.team.view` on the whole client, and the page offers the tab only to whoever holds it.
// The pure half is `utils/cierreEquipo.js`.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Activity, AlarmClock, ArrowLeftRight, Building2, CalendarClock, CalendarSearch, ChevronLeft, ChevronRight, Clock, Download, Inbox, ListChecks, Map as MapIcon,
  Presentation, Timer, Users, X,
} from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { getCierreTeamActivity } from "../../../ApiCalls/mantisBusiness";
import { conReintento } from "../../../utils/conReintento";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { fechaDMY } from "../../../utils/cierrePanel";
import { nombreDeAlerta } from "../../../utils/cierreNombres";
import { descargarTablas } from "../../../utils/excelTabla";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import {
  DIAS_COMPLETOS, DIAS_DE_SEMANA, HORARIO, PERIODOS_EQUIPO, diaCorto, franjaDeHora, TIPOS_DE_TRABAJO, TIPO_DE_ALERTA, accionesPorDia, colaPorRegion, hojasDelEquipo, horaBogota, horasLegibles,
  mapaDeHoras, nombreDePersona, periodoDelEquipo, personasDelEquipo, resumenDelEquipo, serieDeFlujo, serieDeRespuesta, trabajoPorTipo,
} from "../../../utils/cierreEquipo";
import { descargarPresentacionEquipo } from "../../../utils/cierreEquipoPptx";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { guia, guiaDe } from "../../../utils/cierreTours";

const EJE = { tickLine: false, axisLine: false, fontSize: 11 };
const SERIE = ["#0f172a", "#3d5a99", "#6f86c2", "#93a3c4", "#c3ccdf", "#9aa5b8"];
const EDADES = { hoy: "#93a3c4", d1a3: "#f2b33d", d3a7: "#e07d20", mas7: "#d64545" };
const RANGOS = { menos4h: "#3f9a64", hasta24h: "#93c47d", hasta3d: "#f2b33d", mas3d: "#d64545" };
const TIPOS = { descuadres: "#d64545", revisiones: "#f2b33d", faltantes: "#c3ccdf" };
const TRABAJO = ["#0f172a", "#3d5a99", "#6f86c2", "#3f9a64", "#e07d20", "#c3ccdf"];
const TH = "px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground";
const TD = "px-3 py-2";
const POR_PAGINA = 10;
const INPUT = "h-9 rounded-[8px] border bg-card px-2 text-[12.5px] text-brinks-foreground shadow-brinks-card";

// One headline figure, as the Cliente tab draws it: icon, label, the figure, what it means, and its
// trend over the period's days. The `hero` card is in the primary's dark so the eye lands there.
function Indicador({ icono: Icono, etiqueta, valor, detalle, tendencia, hero, tono }) {
  return (
    <article
      {...guiaDe(etiqueta, detalle)}
      className={cn(
        "relative flex min-h-[124px] min-w-0 flex-col overflow-hidden rounded-[14px] border px-4 pb-2 pt-3.5 shadow-brinks-card",
        hero ? "border-transparent bg-primary text-primary-foreground" : "bg-card"
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-[8px]", hero ? "bg-white/10 text-white" : tono)}>
          <Icono className="h-[15px] w-[15px]" />
        </span>
        <p className={cn("truncate text-[12px] font-medium", hero ? "text-white/70" : "text-brinks-muted-foreground")}>{etiqueta}</p>
      </div>
      <p className={cn("mt-2 font-lora font-semibold leading-none tabular-nums", hero ? "text-[30px] text-white" : "text-[24px] text-brinks-foreground")}>{valor}</p>
      {detalle ? <p className={cn("mt-1.5 text-[11.5px]", hero ? "text-white/65" : "text-brinks-muted-foreground")}>{detalle}</p> : null}
      {tendencia && tendencia.length > 1 ? (
        <div className="-mx-4 mt-auto h-9 pt-1.5" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tendencia} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <Area
                type="monotone"
                dataKey="valor"
                stroke={hero ? "#ffffff" : "hsl(var(--primary))"}
                strokeWidth={1.5}
                fill={hero ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.07)"}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </article>
  );
}

// A titled card, as the Cliente tab's. `cuerpo` replaces the padded body; `extra` sits at the right.
function Grafica({ icono: Icono, titulo, subtitulo, extra, cuerpo, children }) {
  return (
    <section {...guiaDe(titulo, subtitulo)} className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card" aria-label={titulo}>
      <header className="flex flex-wrap items-start gap-3 border-b px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-brinks-surface text-brinks-foreground">
          <Icono className="h-4 w-4" />
        </span>
        <div className="min-w-[min(100%,12rem)] flex-1">
          <h2 className="text-[14px] font-semibold text-brinks-foreground">{titulo}</h2>
          {subtitulo ? <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">{subtitulo}</p> : null}
        </div>
        {extra ? <div className="shrink-0">{extra}</div> : null}
      </header>
      {cuerpo || <div className="min-w-0 px-4 py-3">{children}</div>}
    </section>
  );
}

const Vacio = ({ children }) => <p className="py-10 text-center text-[12px] text-brinks-muted-foreground">{children}</p>;

const Conteo = ({ n }) => <span className="rounded-full bg-brinks-surface px-2 py-0.5 text-[11px] tabular-nums text-brinks-muted-foreground">{n}</span>;

// A table's pager, inside its card.
function Pagina({ pagina, total, onPagina }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  if (paginas <= 1) return null;
  const desde = (pagina - 1) * POR_PAGINA + 1;
  return (
    <div className="flex items-center justify-between gap-2 border-t px-4 py-2 text-[11.5px] text-brinks-muted-foreground">
      <span className="tabular-nums">{desde}–{Math.min(total, desde + POR_PAGINA - 1)} de {total}</span>
      <span className="flex items-center gap-1">
        <button type="button" aria-label="Página anterior" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-[48px] text-center tabular-nums">{pagina} / {paginas}</span>
        <button type="button" aria-label="Página siguiente" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

const enPagina = (lista, n) => lista.slice((n - 1) * POR_PAGINA, n * POR_PAGINA);

// Two choices side by side (a chart's view), the active one filled.
function Alternar({ opciones, valor, onCambio, etiqueta }) {
  return (
    <div className="flex items-center gap-0.5 rounded-[8px] border bg-card p-0.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.clave}
          type="button"
          aria-pressed={valor === o.clave}
          onClick={() => onCambio(o.clave)}
          className={cn("rounded-[6px] px-2.5 py-1 text-[11.5px] font-medium transition", valor === o.clave ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground")}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

// The map's colour: a blue that deepens with the actions, white figures on the darker half.
const intensidad = (n, max) => (n && max ? 0.15 + 0.85 * (n / max) : 0);
const fondoDeCelda = (n, max) => (n ? `rgba(61, 90, 153, ${intensidad(n, max)})` : "var(--brinks-surface)");

// When the team works: what it says first (the busiest hour and day, the work outside office hours),
// then the weekday × hour table with the number in each cell and each day's and hour's total.
function MapaDeHoras({ mapa }) {
  if (!mapa.total) return <Vacio>No hay acciones de personas en el periodo.</Vacio>;
  const fuera = mapa.pctFueraDeHorario;
  const hallazgos = [
    { clave: "hora", etiqueta: "Hora con más trabajo", valor: franjaDeHora(mapa.horaPico.hora), detalle: `${mapa.horaPico.n} acciones en el periodo` },
    { clave: "dia", etiqueta: "Día con más trabajo", valor: mapa.diaPico.dia.charAt(0).toUpperCase() + mapa.diaPico.dia.slice(1), detalle: `${Math.round((mapa.diaPico.n / mapa.total) * 100)} % de las acciones` },
    {
      clave: "fuera",
      etiqueta: "Fuera del horario",
      valor: `${fuera} %`,
      detalle: `${mapa.fueraDeHorario} acciones antes de las ${HORARIO.desde}, desde las ${HORARIO.hasta} o en fin de semana`,
      tono: fuera >= 20 ? "text-brinks-danger" : fuera >= 10 ? "text-brinks-warning" : "text-brinks-success",
    },
  ];
  return (
    <div className="space-y-3">
      <ul className="grid gap-2 sm:grid-cols-3">
        {hallazgos.map((h) => (
          <li key={h.clave} className="rounded-[10px] bg-brinks-surface px-3 py-2">
            <span className="block text-[11px] text-brinks-muted-foreground">{h.etiqueta}</span>
            <span className={cn("block font-lora text-[17px] font-semibold text-brinks-foreground", h.tono)}>{h.valor}</span>
            <span className="block text-[10.5px] leading-tight text-brinks-muted-foreground">{h.detalle}</span>
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-[3px] text-[10.5px]" aria-label="Acciones por día y hora">
          <thead>
            <tr>
              <th aria-label="Día" />
              {mapa.horas.map((h) => (
                <th key={h} className={cn("font-medium tabular-nums", h < HORARIO.desde || h >= HORARIO.hasta ? "text-brinks-muted-foreground/60" : "text-brinks-muted-foreground")}>
                  {h}h
                </th>
              ))}
              <th className="pl-1 text-right font-semibold text-brinks-muted-foreground">Total</th>
            </tr>
          </thead>
          <tbody>
            {mapa.celdas.map((fila, d) => (
              <tr key={DIAS_DE_SEMANA[d]}>
                <th className="pr-1 text-left font-medium text-brinks-muted-foreground">{DIAS_DE_SEMANA[d]}</th>
                {fila.map((n, i) => (
                  <td
                    key={mapa.horas[i]}
                    title={`${DIAS_COMPLETOS[d]} de ${franjaDeHora(mapa.horas[i])}: ${n} ${n === 1 ? "acción" : "acciones"}`}
                    className={cn("h-7 min-w-[26px] rounded-[4px] text-center font-semibold tabular-nums", intensidad(n, mapa.max) > 0.55 ? "text-white" : "text-brinks-foreground")}
                    style={{ background: fondoDeCelda(n, mapa.max) }}
                  >
                    {n || ""}
                  </td>
                ))}
                <td className="pl-1 text-right font-semibold tabular-nums text-brinks-foreground">{mapa.porDia[d] || "—"}</td>
              </tr>
            ))}
            <tr>
              <th className="pr-1 text-left font-semibold text-brinks-muted-foreground">Total</th>
              {mapa.porHora.map((n, i) => (
                <td key={mapa.horas[i]} className="text-center font-semibold tabular-nums text-brinks-foreground">{n || ""}</td>
              ))}
              <td className="pl-1 text-right font-semibold tabular-nums text-brinks-foreground">{mapa.total}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-brinks-muted-foreground">
        <span className="flex items-center gap-1.5">
          Menos
          {[0.15, 0.35, 0.55, 0.75, 1].map((a) => <span key={a} className="h-3 w-5 rounded-[3px]" style={{ background: `rgba(61, 90, 153, ${a})` }} />)}
          Más
        </span>
        <span>Hora de Bogotá. Cada acción es atender una alerta, corregir un dato, subir un documento o avisar a una oficina; procesar el día no cuenta aquí.</span>
      </div>
    </div>
  );
}

// A chart's click: the point clicked, from recharts' chart-level event (`activePayload`).
const puntoDe = (evento) => evento?.activePayload?.[0]?.payload || null;
const CLICABLE = { cursor: "pointer" };
// What a day's tooltip adds, so the click is not a guess.
const PISTA = (etiqueta) => `${etiqueta} · clic para ver el detalle`;

// B-139 — one day of the period, opened from any chart drawn by day: its alerts in and out, who
// attended them and how long they waited, who did what, and the closings of that date.
function DetalleDelDia({ dia, datos, persona, onCerrar, onVerPanel }) {
  const ref = useRef(null);
  // Opened under the chart that was clicked, and brought into view: a panel that opens off screen
  // reads as a click that did nothing.
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [dia]);
  const flujo = (datos.flow || []).find((d) => d.dia === dia);
  const proceso = datos.days.find((d) => d.dia === dia);
  const acciones = datos.people
    .filter((p) => !persona || p.persona === persona)
    .map((p) => ({ persona: p.persona, acciones: (p.porDia || []).find((d) => d.dia === dia)?.acciones || 0 }))
    .filter((p) => p.acciones);
  const esperas = (datos.responseDays || []).filter((r) => r.dia === dia && (!persona || r.persona === persona));
  const cifras = [
    { etiqueta: "Descuadres levantados", n: flujo?.descuadres || 0, tono: "text-brinks-danger" },
    { etiqueta: "Por revisar", n: flujo?.revisiones || 0, tono: "text-brinks-warning" },
    { etiqueta: "Falta información", n: flujo?.faltantes || 0 },
    { etiqueta: persona ? "Atendidas por esta persona" : "Atendidas por el equipo", n: persona ? flujo?.atendidasPor?.[persona] || 0 : flujo?.atendidas || 0 },
    { etiqueta: "Resueltas por los datos", n: flujo?.porDatos || 0, tono: "text-brinks-success" },
  ];
  return (
    <section ref={ref} aria-label={`Detalle del ${fechaDMY(dia)}`} className="overflow-hidden rounded-[14px] border border-primary/30 bg-card shadow-brinks-card animate-in fade-in">
      <header className="flex flex-wrap items-center gap-3 border-b bg-brinks-surface px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-primary text-primary-foreground">
          <CalendarSearch className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold text-brinks-foreground">Detalle del {fechaDMY(dia)}</h2>
          <p className="text-[12px] text-brinks-muted-foreground">Lo que pasó ese día{persona ? ` con ${nombreDePersona(persona)}` : ""}.</p>
        </div>
        <Button size="sm" variant="outline" className="h-8" onClick={() => onVerPanel(dia)}>Ver los cierres de ese día</Button>
        <button type="button" aria-label="Cerrar el detalle del día" onClick={onCerrar} className="grid h-8 w-8 place-items-center rounded-[8px] border bg-card hover:bg-brinks-surface-hover">
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="grid gap-4 px-4 py-3 lg:grid-cols-3">
        <ul className="grid grid-cols-2 gap-2 lg:col-span-1">
          {cifras.map((c) => (
            <li key={c.etiqueta} className="rounded-[10px] bg-brinks-surface px-3 py-2">
              <span className={cn("block font-lora text-[20px] font-semibold tabular-nums text-brinks-foreground", c.tono)}>{c.n}</span>
              <span className="text-[11px] text-brinks-muted-foreground">{c.etiqueta}</span>
            </li>
          ))}
        </ul>
        <div className="min-w-0">
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Quién trabajó</h3>
          {acciones.length || esperas.length ? (
            <ul className="divide-y text-[12.5px]">
              {[...new Set([...acciones.map((a) => a.persona), ...esperas.map((e) => e.persona)])].map((p) => {
                const a = acciones.find((x) => x.persona === p);
                const e = esperas.find((x) => x.persona === p);
                return (
                  <li key={p} className="flex items-center justify-between gap-2 py-1.5">
                    <span className="truncate font-medium text-brinks-foreground">{nombreDePersona(p)}</span>
                    <span className="shrink-0 text-[11.5px] tabular-nums text-brinks-muted-foreground">
                      {a ? `${a.acciones} acciones` : ""}{a && e ? " · " : ""}{e ? `${e.atendidas} atendidas, espera ${horasLegibles(e.promedioHoras)}` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-[12px] text-brinks-muted-foreground">Nadie trabajó ese día.</p>}
        </div>
        <div className="min-w-0">
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Cierres con fecha {fechaDMY(dia)}</h3>
          {proceso ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
              <dt className="text-brinks-muted-foreground">Procesado por</dt>
              <dd className="text-brinks-foreground">{proceso.procesadoPor ? `${proceso.viaCorreo ? "Por correo" : nombreDePersona(proceso.procesadoPor)} · ${horaBogota(proceso.fin)}` : "Sin procesar"}</dd>
              <dt className="text-brinks-muted-foreground">Oficinas leídas</dt>
              <dd className="tabular-nums">{proceso.leidas} · {proceso.conError} con error · {proceso.sinFilas} sin filas</dd>
              <dt className="text-brinks-muted-foreground">Cuadran</dt>
              <dd className="tabular-nums">{proceso.cuadrados} de {proceso.cierres}</dd>
              <dt className="text-brinks-muted-foreground">Correo del día</dt>
              <dd>{proceso.correoDelDia ? horaBogota(proceso.correoDelDia) : "—"}</dd>
            </dl>
          ) : <p className="text-[12px] text-brinks-muted-foreground">No hay cierres con esa fecha.</p>}
        </div>
      </div>
    </section>
  );
}

export function EquipoCierres({ token, mantisBusinessId, clientCode, nombreCliente, puedeExportar }) {
  const navigate = useNavigate();
  const hoy = useMemo(() => getColombiaDateString(), []);
  const nombres = useNombresCierre(token, mantisBusinessId, clientCode);
  const [clavePeriodo, setClavePeriodo] = useState("todo");
  const [mes, setMes] = useState(hoy.slice(0, 7));
  const [rangoEscrito, setRangoEscrito] = useState({ desde: "", hasta: "" });
  const [persona, setPersona] = useState(null);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [intento, setIntento] = useState(0);
  const [cierre, setCierre] = useState(null);
  const [conFaltantes, setConFaltantes] = useState(false);
  const [paginas, setPaginas] = useState({ oficinas: 1, dias: 1, reglas: 1 });
  const [generando, setGenerando] = useState(null);
  const [errorArchivo, setErrorArchivo] = useState(null);
  // What a click on a chart chose: one day of the period, one region of the queue.
  // `{ dia, origen }`: the detail opens right under the chart or table it was chosen on.
  const [eleccion, setEleccion] = useState(null);
  const dia = eleccion?.dia || null;
  const [region, setRegion] = useState(null);

  const pedido = useMemo(
    () => periodoDelEquipo(clavePeriodo, hoy, { mes, desde: rangoEscrito.desde, hasta: rangoEscrito.hasta }),
    [clavePeriodo, hoy, mes, rangoEscrito.desde, rangoEscrito.hasta]
  );

  useEffect(() => {
    if (!clientCode || pedido.error) return undefined;
    let activo = true;
    setCargando(true);
    setError(null);
    conReintento(() => getCierreTeamActivity(token, mantisBusinessId, clientCode, pedido.todo ? null : pedido.desde, pedido.hasta))
      .then((r) => {
        if (!activo) return;
        if (r?.success) {
          setDatos(r.data);
          setPaginas({ oficinas: 1, dias: 1, reglas: 1 });
          setEleccion(null);
        } else setError(r?.message || "No se pudo leer el tablero del equipo.");
      })
      .catch(() => activo && setError("No se pudo leer el tablero del equipo."))
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, pedido.todo, pedido.desde, pedido.hasta, pedido.error, intento]);

  // The period as answered: "Todo" starts where the backend says, at the client's first closing.
  const periodo = pedido.todo && datos?.all ? { ...pedido, desde: datos.from, hasta: datos.to } : pedido;

  const personas = useMemo(() => personasDelEquipo(datos), [datos]);
  // A person no longer in the answer (another period) is not a filter any more.
  const elegida = persona && personas.includes(persona) ? persona : null;
  const resumen = useMemo(() => (datos ? resumenDelEquipo(datos, elegida) : null), [datos, elegida]);
  const flujo = useMemo(() => serieDeFlujo(datos?.flow, { persona: elegida, conFaltantes }), [datos, elegida, conFaltantes]);
  const respuestaDiaria = useMemo(() => serieDeRespuesta(datos?.responseDays, elegida), [datos, elegida]);
  const gente = useMemo(() => (datos ? datos.people.filter((p) => !elegida || p.persona === elegida) : []), [datos, elegida]);
  const porDia = useMemo(() => accionesPorDia(gente, 5), [gente]);
  const porTipo = useMemo(() => trabajoPorTipo(gente), [gente]);
  const horas = useMemo(() => mapaDeHoras(datos?.hours, elegida), [datos, elegida]);
  const regiones = useMemo(() => colaPorRegion(datos?.backlog), [datos]);
  const respuestas = datos ? datos.responses.personas.filter((p) => !elegida || p.persona === elegida) : [];
  const dias = datos ? datos.days.filter((d) => !elegida || d.procesadoPor === elegida) : [];

  const backlog = datos ? datos.backlog.filter((o) => !region || (o.region || "Sin región") === region) : [];

  const cambiarPagina = (clave) => (n) => setPaginas((p) => ({ ...p, [clave]: n }));
  const elegirPersona = (p) => setPersona(elegida === p ? null : p);
  const abrirDia = (fecha, origen) => setEleccion((actual) => (actual?.dia === fecha && actual.origen === origen ? null : { dia: fecha, origen }));
  const elegirDia = (origen) => (evento) => {
    const punto = puntoDe(evento);
    if (punto?.dia) abrirDia(punto.dia, origen);
  };
  const detalleEn = (origen) => (eleccion?.origen === origen ? (
    <DetalleDelDia dia={eleccion.dia} datos={datos} persona={elegida} onCerrar={() => setEleccion(null)} onVerPanel={verPanel} />
  ) : null);
  // The chosen day, marked on each chart drawn by day.
  const marcaDelDia = dia ? <ReferenceLine x={diaCorto(dia)} stroke="#e07d20" strokeWidth={2} strokeDasharray="4 3" ifOverflow="extendDomain" /> : null;
  const elegirRegion = (evento) => {
    const punto = puntoDe(evento);
    if (!punto?.region) return;
    setRegion((actual) => (actual === punto.region ? null : punto.region));
    setPaginas((p) => ({ ...p, oficinas: 1 }));
  };
  const verPanel = (fecha) => navigate(`/business/backoffice/panel-cierres?fecha=${fecha}`);
  const generar = (tipo) => {
    if (!datos || generando) return;
    setGenerando(tipo);
    setErrorArchivo(null);
    const cliente = nombreCliente || clientCode;
    const opciones = {
      cliente, desde: fechaDMY(periodo.desde), hasta: fechaDMY(periodo.hasta), nombreDeAlerta: (k) => nombreDeAlerta(nombres, k),
    };
    const sufijo = `${cliente} ${periodo.desde} a ${periodo.hasta}${elegida ? ` ${nombreDePersona(elegida)}` : ""}`;
    const trabajo = tipo === "pptx"
      ? descargarPresentacionEquipo(datos, { ...opciones, persona: elegida, nombreArchivo: `Informe del equipo ${sufijo}.pptx` })
      : descargarTablas(`Equipo ${sufijo}.xlsx`, hojasDelEquipo(datos, opciones));
    Promise.resolve(trabajo)
      .catch(() => setErrorArchivo(tipo === "pptx" ? "No se pudo generar la presentación del equipo." : "No se pudo generar el Excel del equipo."))
      .finally(() => setGenerando(null));
  };

  const controles = (
    <div {...guia("Periodo, persona y exportación", "El periodo del trabajo del equipo (hasta 92 días), una persona para ver solo lo suyo, y la descarga en Excel o en presentación.")} className="flex flex-wrap items-center gap-2 lg:justify-end lg:justify-self-end">
      <div className="flex flex-wrap items-center gap-0.5 rounded-[9px] border bg-card p-1 shadow-brinks-card" role="group" aria-label="Periodo del equipo">
        {PERIODOS_EQUIPO.map((p) => (
          <button
            key={p.clave}
            type="button"
            aria-pressed={clavePeriodo === p.clave}
            onClick={() => setClavePeriodo(p.clave)}
            className={cn("rounded-[7px] px-3 py-1.5 text-[12.5px] font-medium transition", clavePeriodo === p.clave ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground")}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>
      {clavePeriodo === "mes" ? (
        <input type="month" aria-label="Mes" className={INPUT} value={mes} max={hoy.slice(0, 7)} onChange={(e) => e.target.value && setMes(e.target.value)} />
      ) : null}
      {clavePeriodo === "rango" ? (
        <span className="flex items-center gap-1.5 text-[12px] text-brinks-muted-foreground">
          <input type="date" aria-label="Desde" className={INPUT} value={rangoEscrito.desde} max={hoy} onChange={(e) => setRangoEscrito((r) => ({ ...r, desde: e.target.value }))} />
          a
          <input type="date" aria-label="Hasta" className={INPUT} value={rangoEscrito.hasta} max={hoy} onChange={(e) => setRangoEscrito((r) => ({ ...r, hasta: e.target.value }))} />
        </span>
      ) : null}
      <select aria-label="Persona" className={cn(INPUT, "w-[170px] shrink-0 pr-7")} value={elegida || ""} onChange={(e) => setPersona(e.target.value || null)} disabled={!personas.length}>
        <option value="">Todo el equipo</option>
        {personas.map((p) => <option key={p} value={p}>{nombreDePersona(p)}</option>)}
      </select>
      {puedeExportar ? (
        <>
          <Button size="sm" variant="outline" className="h-9 gap-1.5" disabled={!datos || cargando || !periodo.desde || Boolean(generando)} onClick={() => generar("excel")}>
            <Download className="h-4 w-4 shrink-0" />
            <span className="truncate">{generando === "excel" ? "Generando…" : "Exportar Excel"}</span>
          </Button>
          <Button size="sm" className="h-9 gap-1.5" disabled={!datos || cargando || !periodo.desde || Boolean(generando)} onClick={() => generar("pptx")}>
            <Presentation className="h-4 w-4 shrink-0" />
            <span className="truncate">{generando === "pptx" ? "Generando…" : "Presentación"}</span>
          </Button>
        </>
      ) : null}
    </div>
  );

  const encabezado = (
    <header className="mb-1 grid grid-cols-1 items-end gap-3 lg:grid-cols-[minmax(260px,1fr)_auto]">
      <div className="min-w-0 shrink">
        <h1 className="font-lora text-[24px] font-semibold text-brinks-foreground">Equipo</h1>
        {/* One line, as Cliente's: the period, and that the queue is today's. */}
        <p className="mt-0.5 truncate text-[13px] text-brinks-muted-foreground" title="La cola de trabajo es siempre la de hoy">
          {periodo.error || !periodo.desde ? "Cómo va el trabajo del equipo de Brinks en este cliente" : (
            <>
              {periodo.todo ? "Todo, del " : "Del "}<span className="font-medium text-brinks-foreground">{fechaDMY(periodo.desde)}</span> al{" "}
              <span className="font-medium text-brinks-foreground">{fechaDMY(periodo.hasta)}</span> · la cola es la de hoy
            </>
          )}
        </p>
      </div>
      {controles}
    </header>
  );

  if (periodo.error || !datos) {
    return (
      <div className="flex flex-col gap-2">
        {encabezado}
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14 text-center">
          {periodo.error ? (
            <p className="text-[12.5px] text-brinks-muted-foreground">{periodo.error}</p>
          ) : error ? (
            <div className="space-y-2">
              <p role="alert" className="text-[12.5px] text-brinks-danger">{error}</p>
              <button type="button" onClick={() => setIntento((n) => n + 1)} className="rounded-[8px] border bg-card px-3 py-1.5 text-[12px] font-medium hover:bg-brinks-surface-hover">Reintentar</button>
            </div>
          ) : <Spinner size="lg" />}
        </div>
      </div>
    );
  }

  const { queue, automatic } = datos;
  const colas = [
    { tipo: "descuadres", etiqueta: "Descuadres" },
    { tipo: "revisiones", etiqueta: "Por revisar" },
    { tipo: "faltantes", etiqueta: "Falta información" },
  ].map((c) => ({ ...c, ...queue.abiertas[c.tipo] }));
  const tendencia = (clave, serie = flujo) => serie.map((d) => ({ valor: d[clave] || 0 }));
  const cuadreDiario = [...datos.days].reverse().map((d) => ({ valor: d.cierres ? (d.cuadrados / d.cierres) * 100 : 0 }));
  const filasPersonas = [...gente, ...(automatic && !elegida ? [automatic] : [])];

  return (
    <div className={cn("flex flex-col gap-2 transition-opacity", cargando && "opacity-60")} aria-busy={cargando}>
      {encabezado}
      {error ? <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12px] text-brinks-danger">{error}</p> : null}
      {errorArchivo ? <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12px] text-brinks-danger">{errorArchivo}</p> : null}
      {elegida ? (
        <div role="status" className="flex items-center justify-between gap-2 rounded-[10px] border bg-brinks-surface px-3 py-2 text-[12.5px]">
          <span className="min-w-0 truncate">
            Mostrando solo el trabajo de <span className="font-semibold text-brinks-foreground">{nombreDePersona(elegida)}</span>
            <span className="text-brinks-muted-foreground"> · {elegida}</span>
          </span>
          <button type="button" onClick={() => setPersona(null)} className="inline-flex shrink-0 items-center gap-1 rounded-[7px] border bg-card px-2.5 py-1 text-[12px] font-medium hover:bg-brinks-surface-hover">
            <X className="h-3.5 w-3.5" /> Todo el equipo
          </button>
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador
          hero
          icono={ListChecks}
          etiqueta={elegida ? "Alertas que atendió" : "Alertas atendidas"}
          valor={String(resumen.atendidas)}
          detalle={resumen.atendidas ? `${resumen.pctEnUnDia} % en menos de un día` : "en el periodo"}
          tendencia={tendencia("atendidas")}
        />
        <Indicador
          icono={Inbox}
          etiqueta="Cola abierta hoy"
          valor={String(resumen.descuadres + resumen.revisiones)}
          detalle={`${resumen.descuadres} descuadres (${resumen.descuadresViejos} con más de 7 días) · ${resumen.revisiones} por revisar`}
          tendencia={tendencia("descuadres", serieDeFlujo(datos.flow))}
          tono="bg-brinks-danger-soft text-brinks-danger"
        />
        <Indicador
          icono={Timer}
          etiqueta="Tiempo promedio de atención"
          valor={horasLegibles(resumen.promedioHoras)}
          detalle="Desde que se levanta la alerta hasta que se atiende"
          tendencia={respuestaDiaria.map((d) => ({ valor: d.promedioHoras || 0 }))}
          tono="bg-brinks-warning-soft text-brinks-warning"
        />
        <Indicador
          icono={CalendarClock}
          etiqueta="Días procesados"
          valor={String(resumen.diasProcesados)}
          detalle={elegida ? `${resumen.acciones} acciones en el periodo` : `${resumen.porCorreo} por correo · ${resumen.personas} ${resumen.personas === 1 ? "persona" : "personas"} trabajando`}
          tendencia={cuadreDiario}
          tono="bg-brinks-surface text-brinks-foreground"
        />
      </section>


      <div className="grid gap-2 xl:grid-cols-[3fr_2fr]">
        <Grafica
          icono={ArrowLeftRight}
          titulo="Entrada y salida de alertas"
          subtitulo={`${elegida ? "Las que se levantaron cada día y las que atendió esta persona." : "Las que se levantaron cada día, las que atendió el equipo y las que resolvieron los datos al llegar."} Toca un día para ver su detalle.`}
          extra={(
            <Alternar
              etiqueta="Alertas que se muestran"
              valor={conFaltantes ? "todas" : "trabajo"}
              onCambio={(v) => setConFaltantes(v === "todas")}
              opciones={[{ clave: "trabajo", etiqueta: "Descuadres y revisiones" }, { clave: "todas", etiqueta: "Todas" }]}
            />
          )}
        >
          {flujo.length ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={flujo} margin={{ top: 6, right: 6, bottom: 0, left: 0 }} onClick={elegirDia("flujo")} style={CLICABLE}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} />
                  <YAxis allowDecimals={false} {...EJE} width={36} />
                  <Tooltip cursor={{ fill: "var(--brinks-surface)" }} labelFormatter={PISTA} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="descuadres" name="Descuadres" stackId="entrada" fill={TIPOS.descuadres} maxBarSize={26} />
                  <Bar dataKey="revisiones" name="Por revisar" stackId="entrada" fill={TIPOS.revisiones} maxBarSize={26} />
                  {conFaltantes ? <Bar dataKey="faltantes" name="Falta información" stackId="entrada" fill={TIPOS.faltantes} maxBarSize={26} /> : null}
                  <Line type="monotone" dataKey="atendidas" name={elegida ? "Atendidas por esta persona" : "Atendidas por el equipo"} stroke="#0f172a" strokeWidth={2.2} dot={false} />
                  {!elegida ? <Line type="monotone" dataKey="porDatos" name="Resueltas por los datos" stroke="#3f9a64" strokeWidth={1.6} strokeDasharray="4 3" dot={false} /> : null}
                  {marcaDelDia}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>No se levantaron alertas en el periodo.</Vacio>}
        </Grafica>

        <Grafica icono={Inbox} titulo="Cola de trabajo" subtitulo="Lo que está abierto hoy, por cuánto lleva esperando.">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={colas} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                <XAxis type="number" allowDecimals={false} {...EJE} />
                <YAxis type="category" dataKey="etiqueta" width={112} {...EJE} />
                <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                {queue.edades.map((e, i) => (
                  <Bar key={e.clave} dataKey={e.clave} name={e.etiqueta} stackId="edad" fill={EDADES[e.clave]} radius={i === queue.edades.length - 1 ? [0, 4, 4, 0] : 0} maxBarSize={22} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 grid grid-cols-3 gap-2 text-center text-[11.5px]">
            {[
              { clave: "c", etiqueta: "Cierres del periodo", n: queue.cierres.total },
              { clave: "k", etiqueta: "Cuadrados", n: queue.cierres.cuadrados },
              { clave: "p", etiqueta: "Por atender", n: queue.cierres.pendientes },
            ].map((c) => (
              <li key={c.clave} className="rounded-[8px] bg-brinks-surface px-2 py-1.5">
                <span className="block font-semibold tabular-nums text-brinks-foreground">{c.n}</span>
                <span className="text-brinks-muted-foreground">{c.etiqueta}</span>
              </li>
            ))}
          </ul>
        </Grafica>
      </div>
      {detalleEn("flujo")}

      <Grafica
        icono={Users}
        titulo="Trabajo por persona"
        subtitulo="Lo que hizo cada persona en el periodo, por tipo de trabajo. Un día procesado cuenta una vez, aunque lea todas las oficinas. Toca una persona para ver solo su trabajo."
        extra={<Conteo n={gente.length} />}
        cuerpo={filasPersonas.length ? (
          <div className="grid gap-0 xl:grid-cols-[2fr_3fr]">
            <div className="border-b px-3 py-3 xl:border-b-0 xl:border-r" style={{ height: Math.max(180, porTipo.length * 38 + 60) }}>
              {porTipo.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={porTipo}
                    layout="vertical"
                    margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
                    onClick={(e) => puntoDe(e)?.persona && elegirPersona(puntoDe(e).persona)}
                    style={CLICABLE}
                  >
                    <XAxis type="number" allowDecimals={false} {...EJE} />
                    <YAxis type="category" dataKey="nombre" width={120} {...EJE} />
                    <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    {TIPOS_DE_TRABAJO.map((t, i) => (
                      <Bar key={t.clave} dataKey={t.clave} name={t.etiqueta} stackId="trabajo" fill={TRABAJO[i]} maxBarSize={20} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : <Vacio>Solo trabajo automático en el periodo.</Vacio>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[12.5px]">
                <thead>
                  <tr className="border-b text-left">
                    <th className={TH}>Persona</th>
                    <th className={cn(TH, "text-right")}>Días</th>
                    <th className={cn(TH, "text-right")}>Alertas</th>
                    <th className={cn(TH, "text-right")}>Correcc.</th>
                    <th className={cn(TH, "text-right")}>Docs.</th>
                    <th className={cn(TH, "text-right")}>Avisos</th>
                    <th className={cn(TH, "text-right")}>Total</th>
                    <th className={cn(TH, "text-right")}>Última actividad</th>
                  </tr>
                </thead>
                <tbody>
                  {filasPersonas.map((p) => {
                    const auto = p === automatic;
                    return (
                      <tr
                        key={p.persona}
                        tabIndex={auto ? undefined : 0}
                        onClick={auto ? undefined : () => elegirPersona(p.persona)}
                        onKeyDown={auto ? undefined : (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), elegirPersona(p.persona))}
                        className={cn(
                          "border-b last:border-0",
                          auto ? "text-brinks-muted-foreground" : "cursor-pointer hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none",
                          elegida === p.persona && "bg-brinks-surface"
                        )}
                      >
                        <td className={TD}>
                          <span className={cn("block font-medium", !auto && "text-brinks-foreground")}>{auto ? "Automático (correo)" : nombreDePersona(p.persona)}</span>
                          {!auto ? <span className="block text-[10.5px] text-brinks-muted-foreground">{p.persona}</span> : null}
                        </td>
                        <td className={cn(TD, "text-right tabular-nums")}>{p.dias}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{p.alertas}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{p.correcciones}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{p.documentos}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{p.avisos}</td>
                        <td className={cn(TD, "text-right font-semibold tabular-nums")}>{p.acciones}</td>
                        <td className={cn(TD, "whitespace-nowrap text-right text-[11.5px] text-brinks-muted-foreground")}>{horaBogota(p.ultimaActividad)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : <Vacio>Nadie trabajó en este cliente en el periodo.</Vacio>}
      />

      <div className="grid gap-2 xl:grid-cols-2">
        <Grafica icono={Activity} titulo="Acciones por día" subtitulo={`${elegida ? "Lo que hizo esta persona cada día." : "Lo que hizo cada persona cada día (las cinco que más trabajaron; el resto en Otros)."} Toca un día para ver su detalle.`}>
          {porDia.dias.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porDia.dias} margin={{ top: 6, right: 4, bottom: 0, left: 0 }} onClick={elegirDia("acciones")} style={CLICABLE}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} />
                  <YAxis allowDecimals={false} {...EJE} width={36} />
                  <Tooltip cursor={{ fill: "var(--brinks-surface)" }} labelFormatter={PISTA} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  {porDia.personas.map((p, i) => (
                    <Bar key={p.clave} dataKey={p.clave} name={p.nombre} stackId="persona" fill={SERIE[i % SERIE.length]} maxBarSize={28} />
                  ))}
                  {marcaDelDia}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>No hay acciones de personas en el periodo.</Vacio>}
        </Grafica>

        <Grafica
          icono={Clock}
          titulo="Cuándo trabaja el equipo"
          subtitulo={`En qué días y horas ${elegida ? "trabaja esta persona" : "se hace el trabajo"}: sirve para ver los picos y cuánto se trabaja fuera del horario (lunes a viernes, ${HORARIO.desde}:00 a ${HORARIO.hasta}:00).`}
        >
          <MapaDeHoras mapa={horas} />
        </Grafica>
      </div>
      {detalleEn("acciones")}

      <div className="grid gap-2 xl:grid-cols-2">
        <Grafica icono={Timer} titulo="Tiempo para atender una alerta" subtitulo="Desde que se levanta hasta que alguien la atiende, por persona.">
          {respuestas.length ? (
            <div className="space-y-2.5">
              {respuestas.map((p) => (
                <div key={p.persona} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[170px_minmax(0,1fr)_150px]">
                  <div className="min-w-0">
                    <p className="truncate text-[12.5px] font-medium text-brinks-foreground">{nombreDePersona(p.persona)}</p>
                    <p className="text-[11px] text-brinks-muted-foreground">{p.atendidas} {p.atendidas === 1 ? "alerta" : "alertas"}</p>
                  </div>
                  <div className="flex h-3 w-full overflow-hidden rounded-full bg-brinks-surface" title={p.rangos.map((r) => `${r.etiqueta}: ${r.n}`).join(" · ")}>
                    {p.rangos.map((r) => (r.n ? <span key={r.clave} className="h-full" style={{ width: `${(r.n / p.atendidas) * 100}%`, background: RANGOS[r.clave] }} /> : null))}
                  </div>
                  <p className="text-[11.5px] tabular-nums text-brinks-muted-foreground sm:text-right">
                    mediana <span className="font-semibold text-brinks-foreground">{horasLegibles(p.medianaHoras)}</span> · prom. {horasLegibles(p.promedioHoras)}
                  </p>
                </div>
              ))}
              <ul className="flex flex-wrap gap-x-4 gap-y-1 border-t pt-2 text-[11px] text-brinks-muted-foreground">
                {datos.responses.rangos.map((r) => (
                  <li key={r.clave} className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: RANGOS[r.clave] }} />
                    {r.etiqueta}
                  </li>
                ))}
              </ul>
            </div>
          ) : <Vacio>Nadie atendió alertas en el periodo.</Vacio>}
        </Grafica>

        <Grafica icono={Timer} titulo="Atención por día" subtitulo="Cuántas alertas se atendieron cada día y cuánto esperaron en promedio.">
          {respuestaDiaria.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={respuestaDiaria} margin={{ top: 6, right: 6, bottom: 0, left: 0 }} onClick={elegirDia("atencion")} style={CLICABLE}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} />
                  <YAxis yAxisId="n" allowDecimals={false} {...EJE} width={32} />
                  <YAxis yAxisId="h" orientation="right" {...EJE} width={40} tickFormatter={(v) => `${v} h`} />
                  <Tooltip formatter={(v, nombre) => (nombre === "Espera promedio" ? horasLegibles(v) : v)} labelFormatter={PISTA} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="n" dataKey="atendidas" name="Atendidas" fill="#93a3c4" maxBarSize={26} />
                  <Line yAxisId="h" type="monotone" dataKey="promedioHoras" name="Espera promedio" stroke="#e07d20" strokeWidth={2.2} dot={{ r: 2.5 }} connectNulls />
                  {dia ? <ReferenceLine yAxisId="n" x={diaCorto(dia)} stroke="#e07d20" strokeWidth={2} strokeDasharray="4 3" ifOverflow="extendDomain" /> : null}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Nadie atendió alertas en el periodo.</Vacio>}
        </Grafica>
      </div>
      {detalleEn("atencion")}

      <div className="grid gap-2 xl:grid-cols-[3fr_2fr]">
        <Grafica
          icono={Building2}
          titulo="Oficinas con más trabajo abierto"
          subtitulo={region ? `Lo abierto hoy en ${region}. Toca una oficina para abrir su cierre más antiguo.` : "Lo que cada oficina tiene abierto hoy, los descuadres primero. Toca una para abrir su cierre más antiguo."}
          extra={region ? (
            <button type="button" onClick={() => setRegion(null)} className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground">
              {region} <X className="h-3 w-3" />
            </button>
          ) : <Conteo n={backlog.length} />}
          cuerpo={backlog.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[12.5px]">
                  <thead>
                    <tr className="border-b text-left">
                      <th className={TH}>Oficina</th>
                      <th className={TH}>Región</th>
                      <th className={cn(TH, "text-right")}>Descuadres</th>
                      <th className={cn(TH, "text-right")}>Por revisar</th>
                      <th className={cn(TH, "text-right")}>Falta info.</th>
                      <th className={cn(TH, "text-right")}>Más antigua</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enPagina(backlog, paginas.oficinas).map((o) => (
                      <tr
                        key={o.officeCode}
                        tabIndex={o.closingId ? 0 : undefined}
                        onClick={o.closingId ? () => setCierre(o.closingId) : undefined}
                        onKeyDown={o.closingId ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setCierre(o.closingId)) : undefined}
                        title={o.closingId ? `Abrir el cierre más antiguo de ${o.officeName} con algo abierto` : undefined}
                        className={cn("border-b last:border-0", o.closingId && "cursor-pointer hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none")}
                      >
                        <td className={TD}>
                          <span className="font-medium text-brinks-foreground">{o.officeName}</span>
                          <span className="ml-1.5 text-[10.5px] text-brinks-muted-foreground">{o.officeCode}</span>
                        </td>
                        <td className={cn(TD, "text-brinks-muted-foreground")}>{o.region || "—"}</td>
                        <td className={cn(TD, "text-right tabular-nums", o.descuadres && "font-semibold text-brinks-danger")}>{o.descuadres}</td>
                        <td className={cn(TD, "text-right tabular-nums", o.revisiones && "text-brinks-warning")}>{o.revisiones}</td>
                        <td className={cn(TD, "text-right tabular-nums text-brinks-muted-foreground")}>{o.faltantes}</td>
                        <td className={cn(TD, "text-right tabular-nums", o.diasMasAntigua > 7 && "font-semibold text-brinks-danger")}>{o.diasMasAntigua ? `${o.diasMasAntigua} d` : "hoy"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagina pagina={paginas.oficinas} total={backlog.length} onPagina={cambiarPagina("oficinas")} />
            </>
          ) : <Vacio>Ninguna oficina tiene trabajo abierto.</Vacio>}
        />

        <Grafica icono={MapIcon} titulo="Cola por región" subtitulo="Lo abierto hoy en cada región. Toca una para ver solo sus oficinas.">
          {regiones.length ? (
            <div style={{ height: Math.max(180, regiones.length * 34 + 50) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={regiones} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }} onClick={elegirRegion} style={CLICABLE}>
                  <XAxis type="number" allowDecimals={false} {...EJE} />
                  <YAxis type="category" dataKey="region" width={110} {...EJE} />
                  <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="descuadres" name="Descuadres" stackId="region" fill={TIPOS.descuadres} maxBarSize={20} />
                  <Bar dataKey="revisiones" name="Por revisar" stackId="region" fill={TIPOS.revisiones} maxBarSize={20} />
                  <Bar dataKey="faltantes" name="Falta información" stackId="region" fill={TIPOS.faltantes} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Nada abierto.</Vacio>}
        </Grafica>
      </div>

      <div className="grid gap-2 xl:grid-cols-2">
        <Grafica
          icono={AlarmClock}
          titulo="Lo que más lleva esperando"
          subtitulo="Descuadres y revisiones abiertos, el más antiguo primero. Toca uno para abrir el cierre."
          cuerpo={queue.masAntiguas.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-[12.5px]">
                <thead>
                  <tr className="border-b text-left">
                    <th className={TH}>Oficina</th>
                    <th className={TH}>Fecha</th>
                    <th className={TH}>Alerta</th>
                    <th className={cn(TH, "text-right")}>Esperando</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.masAntiguas.map((a) => (
                    <tr
                      key={a.alertId}
                      tabIndex={0}
                      onClick={() => setCierre(a.closingId)}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setCierre(a.closingId))}
                      title={`Abrir el cierre de ${a.officeName} del ${fechaDMY(a.businessDate)}`}
                      className="cursor-pointer border-b last:border-0 hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none"
                    >
                      <td className={TD}>
                        <span className="font-medium text-brinks-foreground">{a.officeName}</span>
                        <span className="ml-1.5 text-[10.5px] text-brinks-muted-foreground">{a.officeCode}</span>
                      </td>
                      <td className={cn(TD, "whitespace-nowrap tabular-nums")}>{fechaDMY(a.businessDate)}</td>
                      <td className={cn(TD, "max-w-[220px] truncate text-brinks-muted-foreground")} title={a.message}>{nombreDeAlerta(nombres, a.ruleKey)}</td>
                      <td className={cn(TD, "text-right font-semibold tabular-nums", a.diasAbierta > 7 ? "text-brinks-danger" : "text-brinks-foreground")}>
                        {a.diasAbierta ? `${a.diasAbierta} d` : "hoy"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Vacio>No hay descuadres ni revisiones abiertas.</Vacio>}
        />

        <Grafica
          icono={ListChecks}
          titulo="Alertas por tipo"
          subtitulo="Cada tipo de alerta levantada en el periodo: cuántas atendió el equipo, cuántas resolvieron los datos y cuántas siguen abiertas."
          extra={<Conteo n={datos.rules.length} />}
          cuerpo={datos.rules.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[12.5px]">
                  <thead>
                    <tr className="border-b text-left">
                      <th className={TH}>Alerta</th>
                      <th className={cn(TH, "text-right")}>Levant.</th>
                      <th className={cn(TH, "text-right")}>Atend.</th>
                      <th className={cn(TH, "text-right")}>Datos</th>
                      <th className={cn(TH, "text-right")}>Abiertas</th>
                      <th className={cn(TH, "text-right")}>Mediana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enPagina(datos.rules, paginas.reglas).map((r) => (
                      <tr key={`${r.ruleKey}-${r.severity}`} className="border-b last:border-0">
                        <td className={cn(TD, "max-w-[240px]")} title={r.ejemplo || undefined}>
                          <span className="block truncate font-medium text-brinks-foreground">{nombreDeAlerta(nombres, r.ruleKey)}</span>
                          <span className="text-[10.5px] text-brinks-muted-foreground">{TIPO_DE_ALERTA[r.severity] || r.severity}</span>
                        </td>
                        <td className={cn(TD, "text-right tabular-nums")}>{r.levantadas}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{r.atendidas}</td>
                        <td className={cn(TD, "text-right tabular-nums text-brinks-muted-foreground")}>{r.porDatos}</td>
                        <td className={cn(TD, "text-right tabular-nums", r.abiertas && r.severity === "alert" && "font-semibold text-brinks-danger")}>{r.abiertas}</td>
                        <td className={cn(TD, "whitespace-nowrap text-right tabular-nums")}>{horasLegibles(r.medianaHoras)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagina pagina={paginas.reglas} total={datos.rules.length} onPagina={cambiarPagina("reglas")} />
            </>
          ) : <Vacio>No se levantaron alertas en el periodo.</Vacio>}
        />
      </div>

      <Grafica
        icono={CalendarClock}
        titulo="Flujo de cada día"
        subtitulo={`${elegida ? "Los días que procesó esta persona." : "Quién procesó el día y cuándo, cómo se leyeron las oficinas y cuándo salió el correo del día."} Toca un día para ver su detalle.`}
        extra={<Conteo n={dias.length} />}
        cuerpo={dias.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-[12.5px]">
                <thead>
                  <tr className="border-b text-left">
                    <th className={TH}>Día</th>
                    <th className={TH}>Procesado por</th>
                    <th className={cn(TH, "text-right")}>Oficinas leídas</th>
                    <th className={cn(TH, "text-right")}>Con error</th>
                    <th className={cn(TH, "text-right")}>Sin filas</th>
                    <th className={TH}>Cuadran</th>
                    <th className={TH}>Correo del día</th>
                    <th className={cn(TH, "text-right")}>Correos de oficinas</th>
                  </tr>
                </thead>
                <tbody>
                  {enPagina(dias, paginas.dias).map((d) => {
                    const pct = d.cierres ? Math.round((d.cuadrados / d.cierres) * 100) : null;
                    return (
                      <tr
                        key={d.dia}
                        tabIndex={0}
                        onClick={() => abrirDia(d.dia, "dias")}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), abrirDia(d.dia, "dias"))}
                        className={cn("cursor-pointer border-b last:border-0 hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none", dia === d.dia && "bg-brinks-surface")}
                      >
                        <td className={cn(TD, "whitespace-nowrap font-medium tabular-nums text-brinks-foreground")}>{fechaDMY(d.dia)}</td>
                        <td className={TD}>
                          {d.procesadoPor ? (
                            <>
                              <span className="text-brinks-foreground">{d.viaCorreo ? "Por correo" : nombreDePersona(d.procesadoPor)}</span>
                              <span className="ml-1.5 text-[11px] text-brinks-muted-foreground">{horaBogota(d.fin)}{d.corridas > 1 ? ` · ${d.corridas} veces` : ""}</span>
                            </>
                          ) : <span className="text-brinks-warning">Sin procesar</span>}
                        </td>
                        <td className={cn(TD, "text-right tabular-nums")}>{d.leidas}</td>
                        <td className={cn(TD, "text-right tabular-nums", d.conError && "font-semibold text-brinks-danger")}>{d.conError}</td>
                        <td className={cn(TD, "text-right tabular-nums", d.sinFilas && "text-brinks-warning")}>{d.sinFilas}</td>
                        <td className={TD}>
                          {pct === null ? "—" : (
                            <span className="flex items-center gap-2">
                              <span className="flex h-1.5 w-16 overflow-hidden rounded-full bg-brinks-surface">
                                <span className="h-full bg-brinks-success" style={{ width: `${pct}%` }} />
                              </span>
                              <span className="tabular-nums">{pct} %</span>
                            </span>
                          )}
                        </td>
                        <td className={cn(TD, "whitespace-nowrap text-[11.5px] text-brinks-muted-foreground")}>{d.correoDelDia ? horaBogota(d.correoDelDia) : "—"}</td>
                        <td className={cn(TD, "text-right tabular-nums")}>{d.correosDeOficinas}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagina pagina={paginas.dias} total={dias.length} onPagina={cambiarPagina("dias")} />
          </>
        ) : <Vacio>{elegida ? "Esta persona no procesó días en el periodo." : "No hay cierres en el periodo."}</Vacio>}
      />
      {detalleEn("dias")}

      <CierreDetailDialog closingId={cierre} token={token} mantisBusinessId={mantisBusinessId} onOpenChange={(open) => !open && setCierre(null)} />
    </div>
  );
}

export default EquipoCierres;
